import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCheckoutStatus } from '../services/api';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('polling');
  const navigate = useNavigate();
  const pollCount = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      navigate('/pricing');
      return;
    }

    const poll = async () => {
      if (pollCount.current >= 10) {
        setStatus('timeout');
        return;
      }
      pollCount.current += 1;

      try {
        const res = await getCheckoutStatus(sessionId);
        if (res.data.payment_status === 'paid' || res.data.already_processed) {
          setStatus('success');
          toast.success('Subscription activated!');
          return;
        }
        if (res.data.status === 'expired') {
          setStatus('expired');
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };

    poll();
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#F7F6F2' }}>
      <div className="bg-white rounded-2xl p-10 text-center max-w-md w-full mx-4" style={{ border: '1px solid #E5E3DB', boxShadow: '0 8px 30px rgba(0,0,0,0.06)' }} data-testid="subscription-success-page">
        {status === 'polling' && (
          <>
            <Loader2 size={48} className="mx-auto mb-4 animate-spin" style={{ color: '#D98A4A' }} />
            <h2 className="font-heading text-2xl font-medium mb-2" style={{ color: '#1A1A1A' }}>Processing Payment</h2>
            <p className="font-body text-sm" style={{ color: '#4B5563' }}>Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(45,106,79,0.1)' }}>
              <CheckCircle2 size={32} style={{ color: '#2D7A5D' }} />
            </div>
            <h2 className="font-heading text-2xl font-medium mb-2" style={{ color: '#1A1A1A' }}>Subscription Active!</h2>
            <p className="font-body text-sm mb-6" style={{ color: '#4B5563' }}>Your payment was successful. Welcome to AI Snugglemind!</p>
            <Button onClick={() => navigate('/dashboard')} className="h-12 px-8 rounded-full font-body font-medium text-white" style={{ background: '#0A3622' }} data-testid="go-to-dashboard-btn">
              Go to Dashboard
            </Button>
          </>
        )}

        {(status === 'expired' || status === 'timeout') && (
          <>
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(157,2,8,0.1)' }}>
              <XCircle size={32} style={{ color: '#0A3622' }} />
            </div>
            <h2 className="font-heading text-2xl font-medium mb-2" style={{ color: '#1A1A1A' }}>
              {status === 'expired' ? 'Payment Expired' : 'Payment Timeout'}
            </h2>
            <p className="font-body text-sm mb-6" style={{ color: '#4B5563' }}>
              {status === 'expired' ? 'Your checkout session expired.' : 'We could not confirm your payment. Please check your email for confirmation.'}
            </p>
            <Button onClick={() => navigate('/pricing')} className="h-12 px-8 rounded-full font-body font-medium text-white" style={{ background: '#0A3622' }} data-testid="retry-payment-btn">
              Try Again
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
